import { z } from "zod";

// Request/response contract for POST /v1/anonymous/codes/render.
// Kept hand-written so we can validate user-input at the form boundary
// without waiting for the backend OpenAPI spec to be fetched (WP2). The
// server-side schema in AnonymousCodeController.kt is the authority —
// PayloadDto + DesignDto from QrCodeDtos.kt (reused by the anon endpoint)
// accept the full 8-variant shape below plus QrFrame.

// Mirror of backend QrFrame (modules/qrcode/.../domain/QrDesign.kt). Only
// NONE + SIMPLE are currently wired all the way through the renderer on
// both sides; the other three variants are reserved for a future sprint
// (see docs/audits/2026-04-22-watermark-frame-audit.md).
export const AnonymousFrame = [
  "NONE",
  "SIMPLE",
  "ROUNDED",
  "SHIELD",
  "CALL_TO_ACTION",
] as const;
export type AnonymousFrame = (typeof AnonymousFrame)[number];

export const AnonymousWifiAuth = ["WPA", "WEP", "NOPASS"] as const;
export type AnonymousWifiAuth = (typeof AnonymousWifiAuth)[number];

// Mirror of backend QrEcc. Error-correction budget: LOW ~7 %, MEDIUM ~15 %,
// QUARTILE ~25 %, HIGH ~30 %. Higher levels make the QR more resilient at
// the cost of denser module matrices (larger code version).
export const AnonymousEcc = ["LOW", "MEDIUM", "QUARTILE", "HIGH"] as const;
export type AnonymousEcc = (typeof AnonymousEcc)[number];

// Must mirror TextPayloadDto @field:Size(max = 400) in QrCodeDtos.kt.
export const TEXT_PAYLOAD_MAX_CHARS = 400;

export const AnonymousDesignSchema = z.object({
  foreground: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .default("#000000"),
  background: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .default("#FFFFFF"),
  frame: z.enum(AnonymousFrame).default("NONE"),
  ecc: z.enum(AnonymousEcc).default("MEDIUM"),
});

export type AnonymousDesign = z.infer<typeof AnonymousDesignSchema>;

export const AnonymousPayloadSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("url"), url: z.string().url().max(2048) }),
  // 400 chars mirrors qrcode-monkey's limit and the backend TextPayloadDto.
  z.object({ type: z.literal("text"), text: z.string().min(1).max(TEXT_PAYLOAD_MAX_CHARS) }),
  z.object({
    type: z.literal("email"),
    to: z.string().email(),
    subject: z.string().max(256).optional(),
    body: z.string().max(2000).optional(),
  }),
  z.object({
    type: z.literal("sms"),
    to: z.string().regex(/^\+?[0-9 .()-]{4,32}$/),
    message: z.string().max(320).optional(),
  }),
  z.object({
    type: z.literal("geo"),
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
  }),
  z.object({
    type: z.literal("wifi"),
    ssid: z.string().min(1).max(64),
    password: z.string().max(128).optional(),
    authentication: z.enum(AnonymousWifiAuth).default("WPA"),
    hidden: z.boolean().default(false),
  }),
  z.object({
    type: z.literal("vcard"),
    fullName: z.string().min(1).max(200),
    email: z.string().email().optional(),
    phone: z.string().regex(/^\+?[0-9 .()-]{4,32}$/).optional(),
    org: z.string().max(200).optional(),
    title: z.string().max(200).optional(),
    url: z.string().max(2048).optional(),
  }),
  z.object({
    type: z.literal("file"),
    // Backend tolerates any non-blank string up to 2 KB — a phone that
    // scans the resulting QR would do nothing useful with `/home/...`,
    // so the frontend enforces a parseable URL (http(s)://, file://,
    // mailto:, …) and surfaces the rejection as an inline error before
    // the user can persist a code that won't scan. Mirror this in the
    // backend renderer if we ever add a server-side guard.
    downloadUrl: z.string().url().max(2048),
  }),
]);

export type AnonymousPayload = z.infer<typeof AnonymousPayloadSchema>;

export const AnonymousFormat = ["png", "svg", "pdf"] as const;
export type AnonymousFormat = (typeof AnonymousFormat)[number];

// Must mirror MIN_RENDER_SIZE_PX / MAX_RENDER_SIZE_PX /
// DEFAULT_RENDER_SIZE_PX from modules/qrcode QrImageService.kt.
// Range deckt alle (Anzeigegröße × DPI)-Kombinationen ab: 10 mm
// × 100 DPI ≈ 39 px bis 200 mm × 600 DPI ≈ 4724 px (gekappt auf
// 4800). 600 DPI ist Pro-Feature und nur via Paywall erreichbar.
export const RENDER_SIZE_MIN_PX = 50;
export const RENDER_SIZE_MAX_PX = 4800;
export const RENDER_SIZE_DEFAULT_PX = 750;

export const AnonymousRequestSchema = z.object({
  payload: AnonymousPayloadSchema,
  design: AnonymousDesignSchema.optional(),
  format: z.enum(AnonymousFormat).default("png"),
  withBranding: z.boolean().default(true),
  email: z.string().email().optional(),
  size: z
    .number()
    .int()
    .min(RENDER_SIZE_MIN_PX)
    .max(RENDER_SIZE_MAX_PX)
    .optional(),
});

export type AnonymousRequest = z.infer<typeof AnonymousRequestSchema>;

import type { ProblemDetail } from "./problem";
export type { ProblemDetail } from "./problem";

export type AnonymousResponse =
  | { ok: true; bytes: Blob; mediaType: string }
  | { ok: false; status: number; problem: ProblemDetail };

const MEDIA_TYPE: Record<AnonymousFormat, string> = {
  png: "image/png",
  svg: "image/svg+xml",
  pdf: "application/pdf",
};

/**
 * Wire-format the Kotlin AnonymousRenderDto on the backend actually accepts.
 * The in-memory form uses a flat discriminated union for ergonomics, but
 * the server expects a nested `payload.url / payload.text` shape (see
 * AnonymousCodeController.AnonymousPayloadDto). Keeping the translation
 * in one place means the UI layer stays shape-agnostic.
 */
function toWireBody(req: AnonymousRequest): Record<string, unknown> {
  const p = req.payload;
  let payload: Record<string, unknown>;
  switch (p.type) {
    case "url":
      payload = { url: { url: p.url } };
      break;
    case "text":
      payload = { text: { text: p.text } };
      break;
    case "email":
      payload = {
        email: stripEmpty({ to: p.to, subject: p.subject, body: p.body }),
      };
      break;
    case "sms":
      payload = { sms: stripEmpty({ to: p.to, message: p.message }) };
      break;
    case "geo":
      payload = { geo: { latitude: p.latitude, longitude: p.longitude } };
      break;
    case "wifi":
      payload = {
        wifi: stripEmpty({
          ssid: p.ssid,
          password: p.password,
          authentication: p.authentication,
          hidden: p.hidden,
        }),
      };
      break;
    case "vcard":
      payload = {
        vcard: stripEmpty({
          fullName: p.fullName,
          email: p.email,
          phone: p.phone,
          org: p.org,
          title: p.title,
          url: p.url,
        }),
      };
      break;
    case "file":
      payload = { file: { downloadUrl: p.downloadUrl } };
      break;
  }
  return {
    payload,
    design: req.design,
    format: req.format,
    withBranding: req.withBranding,
    email: req.email,
  };
}

function stripEmpty<T extends Record<string, unknown>>(o: T): T {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(o)) {
    if (v !== undefined && v !== "" && v !== null) out[k] = v;
  }
  return out as T;
}

export async function renderAnonymous(input: AnonymousRequest): Promise<AnonymousResponse> {
  const req = AnonymousRequestSchema.parse(input);
  const res = await fetch("/v1/anonymous/codes/render", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: MEDIA_TYPE[req.format] + ", application/problem+json" },
    body: JSON.stringify(toWireBody(req)),
  });

  if (!res.ok) {
    let problem: ProblemDetail = { status: res.status };
    try {
      problem = (await res.json()) as ProblemDetail;
    } catch {
      // backend may return empty body on 429; stay with status-only
    }
    return { ok: false, status: res.status, problem };
  }

  const bytes = await res.blob();
  return { ok: true, bytes, mediaType: bytes.type || MEDIA_TYPE[req.format] };
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
